import { getConnectionCookie } from 'portal-sdk';
import * as React from 'react';
import { UseQueryOptions, useMutation, useQuery } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import {
  dataSources as StardogDataSources,
  user as StardogUser,
  virtualGraphs as StardogVirtualGraphs,
} from 'stardog';
import {
  BaseConnection,
  getStardogConnection,
  isBrowserAuthConnection,
} from 'vet-bones/utils';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import { useInvalidateConnections } from 'src/ui/containers/endpoints/utils/invalidate';
import {
  Connection,
  DEMO_CONNECTION_INDEX,
  clearConnection,
} from 'src/ui/features/connection';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { getSdk } from 'src/ui/graph/sdk';
import {
  AddConnectionInput,
  Connection as GraphConnection,
  useGetConnectionByIndexQuery,
  useProfileQuery,
} from 'src/ui/graph/types';
import { runWithRefetchConnectionToken } from 'src/ui/hooks/refetchConnectionToken';
import { loggedInUserEmail } from 'src/ui/selectors/loggedInUserEmail';
import { copy } from 'src/ui/templates/copy';
import { buildRoute } from 'src/ui/utils/buildRoute';
import { isPortalEnabled } from 'src/ui/utils/features';
import {
  SSO_TOKEN_EXCHANGE_FAILED,
  SSO_TOKEN_REQUIRED,
  getGraphQLErrorCode,
} from 'src/ui/utils/graphqlErrorCode';

/**
 * Custom profile hook will return the user info. This will refresh
 * the data every time the window gets focus or on a timer set
 * by the refetchInterval.
 */
export const useProfile = () => {
  return useProfileQuery(graphQLClient, undefined, {
    refetchInterval: 30 * 1000,
    retry: false,
    cacheTime: 0,
  });
};

export const RECENT_CONNECTION = (email: string) =>
  `RECENT_CONNECTION-${email}`;
export const RECENT_ORG_CONNECTION = (email: string, org: string) =>
  `RECENT_ORG_CONNECTION-${email}-${org}`;
export const RECENT_WORKSPACE = (email: string) => `RECENT_WORKSPACE-${email}`;

/**
 * Returns an imperative setter for the last visited workspace, keyed by the
 * logged-in user's email, so the root `/` landing can restore it on a later
 * visit. The stored value is the org domain, or an empty string for the
 * personal workspace. Use this from callbacks (e.g. the workspace selector)
 * that must record the choice before navigating; see `useSetLastWorkspace` for
 * the render-driven variant.
 */
export const useRecordLastWorkspace = () => {
  const email = useAppSelector(loggedInUserEmail);
  return React.useCallback(
    (org: string) => {
      if (email) {
        localStorage.setItem(RECENT_WORKSPACE(email), org);
      }
    },
    [email]
  );
};

/**
 * Persists the workspace being viewed to localStorage in a render effect, when
 * `enabled`. Thin wrapper over `useRecordLastWorkspace` for components that
 * record the current workspace as they render.
 */
export const useSetLastWorkspace = (org: string, enabled = true) => {
  const record = useRecordLastWorkspace();
  React.useEffect(() => {
    if (enabled) {
      record(org);
    }
  }, [org, enabled, record]);
};

/**
 * Reads the last visited workspace from localStorage for the logged-in user's
 * email: the org domain, an empty string for the personal workspace, or null
 * when nothing is stored.
 * Reads on render; relies on navigation/query re-renders to refresh (not reactive to writes).
 */
export const useGetLastWorkspace = (): string | null => {
  const email = useAppSelector(loggedInUserEmail);
  if (email) {
    return localStorage.getItem(RECENT_WORKSPACE(email));
  }
  return null;
};

const recentConnectionKey = (email: string, org: string) =>
  org ? RECENT_ORG_CONNECTION(email, org) : RECENT_CONNECTION(email);

/**
 * Persists the last visited connection index to localStorage, keyed by the
 * logged-in user's email and org, so it can be restored on a later visit.
 * The write runs in an effect and only when `enabled` and an `index` exists.
 */
export const useSetLastConnectionIndex = (
  index: number | undefined,
  org: string,
  enabled = true
) => {
  const email = useAppSelector(loggedInUserEmail);
  let key: string | null = null;
  if (email) {
    key = recentConnectionKey(email, org);
  }
  React.useEffect(() => {
    if (enabled && key && index !== undefined) {
      localStorage.setItem(key, String(index));
    }
  }, [index, enabled, key]);
};

/**
 * Reads the last visited connection index from localStorage for the given
 * email and org, or null when none is stored. Usable outside of render, e.g.
 * to resolve an org other than the one in the current route.
 */
export const getLastConnectionIndex = (
  email: string | null,
  org: string
): number | null => {
  if (!email) {
    return null;
  }

  const key = recentConnectionKey(email, org);
  const value = localStorage.getItem(key);
  const parsed = value ? parseInt(value, 10) : null;
  if (parsed !== null && !Number.isNaN(parsed)) {
    return parsed;
  }
  return null;
};

/**
 * Reads the last visited connection index for the logged-in user's email and
 * the given org, or null when none is stored.
 * Reads on render; relies on navigation/query re-renders to refresh (not reactive to writes).
 */
export const useGetLastConnectionIndex = (org: string): number | null => {
  const email = useAppSelector(loggedInUserEmail);
  return getLastConnectionIndex(email, org);
};

/**
 * Custom hook that returns a function to set the connection index
 * then redirect the user to the dashboard page.
 */
export const useNavigateToConnection = (org?: string) => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  return React.useCallback(
    (index: number, path = '') => {
      dispatch(clearConnection());
      history.push(buildRoute(org, index, path));
    },
    [dispatch, history, org]
  );
};

/**
 * Custom hook that returns a function to clear any active connection state
 * then redirect the user to partner home.
 */
export const useNavigateToPartnerConnection = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  return React.useCallback(
    (partnerHomeUrl: string) => {
      dispatch(clearConnection());
      history.push(partnerHomeUrl);
    },
    [dispatch, history]
  );
};

/**
 * Custom hook that returns a function to clear the connection index
 * and redirect the user to the home page.
 */
export const useClearConnection = () => {
  const { org = '' } = useParams<{ org?: string }>();
  const dispatch = useAppDispatch();
  const invalidateConnections = useInvalidateConnections(org);

  const clearConnectionWrapper = () => {
    dispatch(clearConnection());
    invalidateConnections();
  };

  return clearConnectionWrapper;
};

export class SSOTokenError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'SSOTokenError';
    this.code = code;
    Object.setPrototypeOf(this, SSOTokenError.prototype);
  }
}

export const getSSOTokenErrorCode = (error: unknown): string | null => {
  if (error instanceof SSOTokenError) {
    return error.code;
  }
  return null;
};

export const verifyConnectionWithPortalJWT = async (
  connection: Connection,
  org: string
): Promise<AddConnectionInput> => {
  const { endpoint, name } = connection;
  const connectionCookie = getConnectionCookie();
  const sdk = getSdk(graphQLClient);

  let accessToken = '';
  let username = '';

  if (connectionCookie && connectionCookie.isLaunchpad) {
    username = connection.username;
    accessToken = connection.token ?? '';
  } else {
    // For an existing Connection (has id) prefer the routing-aware
    // generateConnectionToken query. The Cloud invitation acceptance flow
    // builds a stub Connection with only an endpoint (no id yet), so it
    // falls back to the legacy endpoint-based generateToken query.
    let oauthToken;
    try {
      oauthToken = connection.id
        ? (await sdk.generateConnectionToken({ connectionId: connection.id }))
            .generateConnectionToken
        : (await sdk.generateToken({ endpoint })).generateToken;
    } catch (error) {
      const code = getGraphQLErrorCode(error);
      if (code === SSO_TOKEN_REQUIRED || code === SSO_TOKEN_EXCHANGE_FAILED) {
        throw new SSOTokenError(
          code,
          (error as Error).message || 'SSO authentication error'
        );
      }
      throw error;
    }

    /* istanbul ignore next: no need to test defensive code */
    if (!oauthToken?.access_token || !oauthToken?.user.username) {
      throw new Error(copy.errors.verificationFailed);
    }
    username = oauthToken.user.username;
    accessToken = oauthToken.access_token;
  }

  const stardogConnection = getStardogConnection(
    {
      endpoint,
      username,
      token: accessToken,
    },
    { agent: 'Portal' }
  );

  const isValid = await runWithRefetchConnectionToken(org)(
    stardogConnection,
    connection.index,
    (stardogConn) => StardogUser.valid(stardogConn)
  );
  if (!isValid.ok) {
    throw new Error(copy.errors.verificationFailed);
  }

  return {
    endpoint,
    name,
    useSSO: true,
    internalEndpoint: connection.internalEndpoint,
  };
};

export const verifyConnectionWithBrowserAuth = async (
  connection: Connection,
  org: string
): Promise<AddConnectionInput> => {
  const { endpoint, name } = connection;

  const stardogConnection = getStardogConnection(connection, {
    agent: 'Portal',
  });

  const isValid = await runWithRefetchConnectionToken(org)(
    stardogConnection,
    connection.index,
    (stardogConn) => StardogUser.valid(stardogConn)
  );
  if (!isValid.ok) {
    throw new Error(copy.errors.verificationFailed);
  }

  return {
    endpoint,
    name,
    useBrowserAuth: true,
    // unset values previously stored on the server (when editing connection)
    username: '',
    token: '',
    internalEndpoint: connection.internalEndpoint,
  };
};

export const verifyUsernamePasswordConnection = async (
  connection: Connection,
  org: string
): Promise<AddConnectionInput> => {
  const { endpoint, name, username, password, useSSO, token } = connection;
  const stardogConnection = getStardogConnection(connection, {
    agent: 'Portal',
  });
  const useBrowserAuth = isBrowserAuthConnection(connection);

  // if there is no password and conn doesn't have bearer token,
  // API call to Stardog will fail anyway with a `401` so fail early.
  // In order to get a token from Stardog to then set token on connection
  // the request needs to contain a password
  if (!password && !token) {
    throw new Error(copy.errors.invalidUserPassword);
  }

  const tokenResponse = await runWithRefetchConnectionToken(
    org
  )(stardogConnection, connection.index, (stardogConn) =>
    StardogUser.token(stardogConn)
  );
  switch (tokenResponse.status) {
    case 200:
      return {
        name,
        endpoint,
        username,
        token: tokenResponse.body.token,
        useBrowserAuth,
        useSSO,
        internalEndpoint: connection.internalEndpoint,
      };
    case 400:
    case 404:
      // The connection does not support token auth but the password is ok
      return {
        name,
        endpoint,
        username,
        useBrowserAuth,
        useSSO,
        internalEndpoint: connection.internalEndpoint,
      };
    case 401:
      throw new Error(copy.errors.invalidUserPassword);
    /* istanbul ignore next: no need to test the default case */
    default:
      return {
        name,
        endpoint,
        username,
        useBrowserAuth,
        useSSO,
        internalEndpoint: connection.internalEndpoint,
      };
  }
};

/**
 * Custom hook that verifies a Stardog connection with username and password
 */
const CONNECTION_TIMEOUT_MS = 10_000;

export class ConnectionTimeoutError extends Error {
  constructor(message: string = copy.errors.connectionFailed) {
    super(message);
    this.name = 'ConnectionTimeoutError';

    Object.setPrototypeOf(this, ConnectionTimeoutError.prototype);
  }
}

export const isConnectionTimeoutError = (
  error: unknown
): error is ConnectionTimeoutError => error instanceof ConnectionTimeoutError;

export const useVerifyConnection = () => {
  const { org = '' } = useParams<{ org?: string }>();
  const verify = async (
    connection: Connection
  ): Promise<AddConnectionInput> => {
    const { useSSO } = connection;

    let result: Promise<AddConnectionInput>;

    // First we need to determine what type of connection to test, if there
    // is no username then we see if they can connection with a portal JWT.
    if (useSSO) {
      result = verifyConnectionWithPortalJWT(connection, org);
    } else if (isBrowserAuthConnection(connection)) {
      result = verifyConnectionWithBrowserAuth(connection, org);
    } else {
      result = verifyUsernamePasswordConnection(connection, org);
    }

    let timeoutId: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new ConnectionTimeoutError()),
        CONNECTION_TIMEOUT_MS
      );
    });

    return Promise.race([result, timeout]).finally(() =>
      clearTimeout(timeoutId)
    );
  };

  const mutation = useMutation<AddConnectionInput, Error, Connection>(verify);

  return mutation;
};

export type CurrentConnection = {
  notFound: boolean;
  connection: Connection | null;
  isLoading: boolean;
};

/**
 * Get the current connection details.
 */
export const useCurrentConnection = (
  connectionIndex: string | undefined,
  orgDomain: string | undefined
): CurrentConnection => {
  const index = Number.parseInt(connectionIndex || '-1', 10);
  const enabled =
    index >= 0 &&
    // Don't allow getting the demo connection for Launchpad
    (isPortalEnabled() || index !== DEMO_CONNECTION_INDEX);

  const { data, isLoading } = useGetConnectionByIndexQuery(
    graphQLClient,
    { index, org_domain: orgDomain },
    { enabled }
  );

  const connection = (data?.connection || null) as Connection | null;
  const notFound = isLoading ? false : typeof connection === 'undefined';

  return { notFound, connection, isLoading };
};

export type CreateDatabricksConnectionInput = {
  connection: GraphConnection;
  databricksConnectionName: string;
  jdbcUrl: string;
  personalAccessToken: string;
  clusterId: string;
};

/**
 * Create Databricks JDBC connection on Stardog server
 */
/* istanbul ignore next */
export const createDatabricksJDBCConnection = async ({
  connection,
  databricksConnectionName,
  jdbcUrl,
  personalAccessToken,
  clusterId,
}: CreateDatabricksConnectionInput): Promise<boolean> => {
  const stardogConnection = getStardogConnection(connection as BaseConnection, {
    agent: 'Portal',
  });
  let externalComputeHostName = '';
  try {
    externalComputeHostName = jdbcUrl.split(':')[2].substring(2);
  } catch (error) {
    console.warn(error);
  }

  /* istanbul ignore next */
  const resp = await runWithRefetchConnectionToken()(
    stardogConnection,
    connection.index,
    (stardogConn) =>
      StardogDataSources.add(stardogConn, databricksConnectionName, {
        'jdbc.url': jdbcUrl,
        'jdbc.username': 'token',
        'jdbc.password': personalAccessToken,
        'jdbc.driver': 'com.databricks.client.jdbc.Driver',
        'sql.schemas': '*.*',
        'sql.catalogs': '*',
        'external.compute': 'true',
        'databricks.cluster.id': clusterId,
        'external.compute.host.name': externalComputeHostName,
        'stardog.host.url': connection.endpoint,
      })
  );
  if (!resp.ok) {
    throw new Error(
      ` Server returned error: "${resp.body?.message}" with ${resp.status} error code.`
    );
  }

  return resp.ok;
};

/**
 * Create Databricks JDBC connection on Stardog server
 */
export const useCreateDatabricksJdbcConnection = (
  options?: UseQueryOptions<boolean>
) => {
  const ret = useMutation<boolean, Error, CreateDatabricksConnectionInput>(
    createDatabricksJDBCConnection,
    options as any
  );
  return ret;
};

export type RemoveDatabricksConnectionInput = {
  connection: GraphConnection;
  databricksConnectionName: string;
};

/**
 * Delete Databricks JDBC DataSource on Stardog server
 */
/* istanbul ignore next */
export const removeDatabricksJDBCDataSource = async ({
  connection,
  databricksConnectionName,
}: RemoveDatabricksConnectionInput): Promise<boolean> => {
  if (!connection || databricksConnectionName === '') {
    return true;
  }
  const stardogConnection = getStardogConnection(connection as BaseConnection, {
    agent: 'Portal',
  });
  const virtualGraphsInfo: VirtualGraphInfo = await getVirtualGraphs(
    connection,
    databricksConnectionName
  );
  virtualGraphsInfo.virtualGraphsForDataSource.forEach(
    async (virtualGraph: string) => {
      const vgRemove = await runWithRefetchConnectionToken()(
        stardogConnection,
        connection.index,
        (stardogConn) => StardogVirtualGraphs.remove(stardogConn, virtualGraph)
      );
      if (!vgRemove.ok) {
        throw new Error(
          ` Server returned error: "${vgRemove.body?.message}" with ${vgRemove.status} error code.`
        );
      }
    }
  );

  /* istanbul ignore next */
  const resp = await runWithRefetchConnectionToken()(
    stardogConnection,
    connection.index,
    (stardogConn) =>
      StardogDataSources.remove(stardogConn, databricksConnectionName)
  );
  if (!resp.ok) {
    throw new Error(
      ` Server returned error: "${resp.body?.message}" with ${resp.status} error code.`
    );
  }

  return resp.ok;
};

/**
 * Create Databricks JDBC connection on Stardog server
 */
export const useRemoveDatabricksJdbcDataSource = (
  options?: UseQueryOptions<boolean>
) => {
  return useMutation<boolean, Error, RemoveDatabricksConnectionInput>(
    removeDatabricksJDBCDataSource,
    options as any
  );
};

export type VirtualGraphInfo = {
  virtualGraphsForDataSource: string[];
};

/**
 * Returns the list of virtual graphs
 */
export const getVirtualGraphs = async (
  connection: GraphConnection,
  datasource: string
): Promise<VirtualGraphInfo> => {
  const stardogConnection = getStardogConnection(connection as BaseConnection, {
    agent: 'Portal',
  });

  const resp = await runWithRefetchConnectionToken()(
    stardogConnection,
    connection.index,
    (stardogConn) => StardogVirtualGraphs.listInfo(stardogConn)
  );
  if (!resp.ok) {
    throw new Error(
      ` Server returned error: "${resp.body?.message}" with ${resp.status} error code.`
    );
  }

  const vgArray = resp.body.virtual_graphs;
  const datasourceArray: string[] = [];
  const targetDataSource: string = 'data-source://'.concat(datasource);

  vgArray.forEach((vg: any) => {
    if (targetDataSource === vg.data_source) {
      datasourceArray.push(vg.name);
    }
  });

  return {
    virtualGraphsForDataSource: datasourceArray,
  };
};

export const useGetVirtualGraphsForDatasource = (
  connection: GraphConnection,
  datasource: string
) => {
  return useQuery(
    'virtualgraphs',
    () => getVirtualGraphs(connection, datasource),
    {
      cacheTime: 0, // disable cache so we don't use stale data
      retry: false,
    }
  );
};
